// Potential matches: the duplicate check as a gate inside Save (DS DC-84, candidate C-7). "Present potential matches
// before save and allow an authorized Recruiter or TA user to use the existing profile or create a distinct record
// with a documented reason." One compare grid per match, rows in his order (Primary email · Secondary email · Name ·
// Phone · LinkedIn URL · Candidate history); the attribute that matched is the gold cell and shows the value entered
// here — the other profile's contact values never reach this screen (candidate.match returns which attributes
// matched, not their values). A match outside the viewer's scope is masked whole (DC-28). Never auto-merge: nothing
// is written until the person decides; a Coordinator sees the grid without a choice, and Save refers the match.

import type { HitOut } from "@/api/tools";
import { Banner, CheckTile, Compare, Field, G1_LABELS, G6_LABELS, G6_RULE_LABELS, Stack, TextArea, type CompareAttribute } from "@/mc";
import { BRD } from "./brd";
import { CANDIDATE_RULES } from "./labels";

/** The values typed (or confirmed) on this screen, compared with each match. */
export interface Entry {
  name: string;
  email: string;
  secondaryEmail: string;
  phone: string;
  linkedinUrl: string;
}

export interface PotentialMatchesProps {
  hits: readonly HitOut[];
  entry: Entry;
  /** A Recruiter, TA Manager or Admin decides; a Coordinator refers. */
  mayResolve: boolean;
  /** The requisition the profile joins (Use existing links the existing profile to it). */
  requisitionId?: string | null | undefined;
  /** Use existing with a requisition in context: link the existing profile to it (the match's per-caller ref). */
  onUseExisting: (hit: HitOut) => void;
  /** Use existing with no requisition in context: open the existing profile (its token). */
  onOpenProfile: (candidateId: string) => void;
  busy?: boolean | undefined;
  distinct: boolean;
  onDistinct: (on: boolean) => void;
  reason: string;
  onReason: (reason: string) => void;
  /** The server found a match this screen did not show (refused at save): the grid was refreshed. */
  refused?: string | null | undefined;
}

const ROWS: readonly { key: string; label: string; reasons: readonly string[]; value: (entry: Entry, hit: HitOut) => string | null }[] = [
  { key: "primary", label: G6_LABELS.primaryEmail, reasons: ["email"], value: (e) => e.email },
  { key: "secondary", label: G6_LABELS.secondaryEmail, reasons: ["secondary_email"], value: (e) => e.secondaryEmail || e.email },
  { key: "name", label: G6_LABELS.name, reasons: ["name"], value: (_e, hit) => hit.display_name ?? null },
  { key: "phone", label: G6_LABELS.phone, reasons: ["phone"], value: (e) => e.phone },
  { key: "linkedin", label: G6_LABELS.linkedinUrl, reasons: ["linkedin"], value: (e) => e.linkedinUrl },
  {
    key: "history", label: G6_LABELS.candidateHistory, reasons: ["history"],
    value: (_e, hit) => hit.applications.map((fact) => `${fact.requisition_id} · ${fact.title} · ${fact.state}`).join(" | ") || null,
  },
];

function attributes(entry: Entry, hit: HitOut): CompareAttribute[] {
  return ROWS.map((row) => {
    const matched = row.reasons.some((reason) => hit.reasons.includes(reason));
    // A contact value is shown only where it matched: it is then the value entered here, never the other profile's.
    const contact = row.key !== "name" && row.key !== "history";
    return {
      key: row.key,
      label: row.label,
      values: [contact && !matched ? null : row.value(entry, hit)],
      matched: matched ? [0] : [],
    };
  });
}

export function PotentialMatches({
  hits, entry, mayResolve, requisitionId, onUseExisting, onOpenProfile, busy, distinct, onDistinct, reason, onReason, refused,
}: PotentialMatchesProps) {
  return (
    <Stack gap={12}>
      {refused ? <Banner tone="red" live>{refused}</Banner> : <Banner tone="warn">{CANDIDATE_RULES.potentialMatch}</Banner>}
      {hits.map((hit, index) => {
        const masked = hit.projection === "masked";
        const open = !masked && !requisitionId && hit.candidate_id ? hit.candidate_id : null;
        return (
          <Compare
            key={hit.match_ref}
            records={[{ id: hit.candidate_no, name: hit.display_name ?? "", masked }]}
            attributes={attributes(entry, hit)}
            useExisting={mayResolve ? {
              onClick: () => {
                if (open) onOpenProfile(open);
                else onUseExisting(hit);
              },
              blocked: masked ? { reason: BRD.links } : undefined,
              busy,
              hs: `cand.match.${index}.use`,
            } : undefined}
          />
        );
      })}
      {mayResolve ? (
        <>
          <CheckTile checked={distinct} onChange={onDistinct} >{G6_LABELS.createDistinct}</CheckTile>
          {distinct ? (
            <Field label={G1_LABELS.reason} required hint={G6_RULE_LABELS.neverAutoMerge}>
              <TextArea rows={2} value={reason} onChange={(event) => onReason(event.target.value)}  />
            </Field>
          ) : null}
        </>
      ) : null}
    </Stack>
  );
}
